import './payment-method-add-sheet.scss';

import Script from 'next/script';
import { ReactElement, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import { Divider } from '@sola/ark-core/atoms/divider/divider.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import { Fieldset } from '@sola/ark-core/molecules/fieldset/fieldset.js';
import {
  FormSubmit,
  FormSubmitProps,
} from '@sola/ark-core/molecules/form/form.js';
import {
  Sheet,
  SheetActions,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTrigger,
} from '@sola/ark-core/molecules/sheet/sheet.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { SupporterErrorTypeEnum } from '@sola/schema/dotcom/supporters/errors.js';
import {
  PaymentMethodTypesEnum,
  SupporterAchDetails,
  SupporterCCDetails,
  SupporterCCFieldsEnum,
  SupporterPaymentMethodDetails,
} from '@sola/schema/dotcom/supporters/paymentMethods.js';
import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.js';
import { getLongExpiryDate } from '@sola/shared-utils/credit-cards.js';
import { userErrorPaymentMethodCreate } from '@sola/shared-utils/errors/supporters.js';
import {
  isPaymentMethodAch,
  isPaymentMethodCard,
} from '@sola/shared-utils/my/payment-method-type-guards.js';
import { usePaymentMethodAdd } from '@sola/supporter-sdk/hooks.js';

import { CommitmentsBadges } from '../../molecules/commitment-badges/commitment-badges';
import { LocalAlert } from '../../molecules/local-alerts/local-alerts-display';
import { useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider';
import { PaymentACHFields } from '../../molecules/payment-method-add-form/payment-ach-fields';
import { PaymentCCFields } from '../../molecules/payment-method-add-form/payment-cc-fields';
import { PaymentMethodAddForm } from '../../molecules/payment-method-add-form/payment-method-add-form';
import { PaymentMethodSwitch } from '../../molecules/payment-method-switch/payment-method-switch';
import { LocalAlertConfig } from '../../types/localeAlerts';
import {
  createPaymentMethodSuccessAlert,
  createSupportAlert,
} from '../../utils/alerts';
import { getRecurringCommitments } from '../../utils/commitments';
import { getErrorTranslationKeys } from '../../utils/errors';

export interface PaymentMethodAddSheetCommitmentListProps {
  commitments: GraphQLCommitment[];
}

export const PaymentMethodAddSheetCommitmentList: React.FC<
  PaymentMethodAddSheetCommitmentListProps
> = ({ commitments }) => {
  const { t } = useTranslation('my');

  return (
    <>
      <Divider purpose="neutral" className="my-comfy-xl" />

      <div className="PaymentMethodAddSheet__commitment-badges flex flex-col lg:flex-row md:flex-row w-full lg:items-center md:items-center gap-comfy-sm justify-between">
        <h3 className="body-sm">{t('addPaymentMethod.commitmentSummary')}</h3>

        <CommitmentsBadges
          commitments={commitments}
          layout="spaced"
          itemsToShow={5}
        />
      </div>
    </>
  );
};

type PaymentMethodAddSheetFooterProps = Pick<FormSubmitProps, 'isBusy'>;

const AddPaymentMethodSheetFooter: React.FC<
  PaymentMethodAddSheetFooterProps
> = ({ isBusy }) => {
  const { t } = useTranslation('my');

  return (
    <SheetFooter>
      <SheetActions>
        <SheetClose asChild>
          <ButtonStyle
            size="lg"
            purpose="neutral"
            variant="secondary"
            fullWidth
          >
            <button type="button">
              {t('addPaymentMethod.sheetAction.cancel')}
            </button>
          </ButtonStyle>
        </SheetClose>

        <FormSubmit
          iconBefore={<ArkIcon iconName="lock-01" />}
          isBusy={isBusy}
          disableUntilFilled
        >
          {t('addPaymentMethod.sheetAction.add')}
        </FormSubmit>
      </SheetActions>
    </SheetFooter>
  );
};

export interface PaymentMethodAddSheetProps {
  commitments?: GraphQLCommitment[];
  /**
   * This should only take in elements that resolve to html buttons.
   */
  openTrigger: ReactElement;
}
export const PaymentMethodAddSheet: React.FC<PaymentMethodAddSheetProps> = ({
  openTrigger,
  commitments = [],
}) => {
  const { t } = useTranslation('my');
  const title = t('addPaymentMethod.form.title');
  const [isOpen, setIsOpen] = useState(false);
  const [sheetAlerts, setSheetAlerts] = useState<LocalAlertConfig[]>([]);
  const { addAlerts, clearAlerts } = useLocalAlerts();
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethodTypesEnum>(
    PaymentMethodTypesEnum.Ach,
  );
  const recurringCommitments = getRecurringCommitments(commitments);
  const addPaymentMethod = usePaymentMethodAdd();

  const removeSheetAlert = (id: string) =>
    setSheetAlerts(sheetAlerts.filter((alert) => alert.id !== id));

  const handleSubmit = async (
    paymentMethodDetails: SupporterPaymentMethodDetails,
  ) => {
    setSheetAlerts([]);
    clearAlerts();

    try {
      const addedPaymentMethod =
        await addPaymentMethod.mutateAsync(paymentMethodDetails);

      if (!addedPaymentMethod) {
        throw userErrorPaymentMethodCreate();
      }

      addAlerts(
        createPaymentMethodSuccessAlert(
          addedPaymentMethod,
          'addPaymentMethod.success.action',
        ),
      );
      setIsOpen(false);
    } catch (errors) {
      const translationKeys = getErrorTranslationKeys(
        SupporterErrorTypeEnum.PaymentMethodCreate,
        errors,
      );
      setSheetAlerts(
        translationKeys.map((translationKey) => {
          return createSupportAlert(translationKey);
        }),
      );
    }
  };

  const handleAchSubmit = async (data: FieldValues) => {
    const achDetails = data as SupporterAchDetails;
    await handleSubmit({ ...achDetails, type: PaymentMethodTypesEnum.Ach });
  };

  const handleCardSubmit = async (data: FieldValues) => {
    const cardDetails = data as SupporterCCDetails;
    const expirationDate = getLongExpiryDate(
      data[SupporterCCFieldsEnum.expirationDate],
    );

    await handleSubmit({
      ...cardDetails,
      expirationDate,
      type: PaymentMethodTypesEnum.Card,
    });
  };

  return (
    <>
      <Script src="/tokenex.js" strategy="lazyOnload" />
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger>{openTrigger}</SheetTrigger>
        <SheetContent title={title}>
          <SheetHeader>{title}</SheetHeader>
          <SheetBody>
            <p
              id="selectPaymentMethodType"
              className="body-sm md:body-md body-neutral-mild"
            >
              {t('addPaymentMethod.form.leadin')}
            </p>

            {sheetAlerts.length > 0 && (
              <div className="mt-comfy-sm md:mt-comfy-lg">
                {sheetAlerts.map((sheetAlert) => (
                  <LocalAlert
                    key={sheetAlert.id}
                    alert={sheetAlert}
                    className="my-comfy-xs"
                    onClose={() => removeSheetAlert(sheetAlert.id)}
                  />
                ))}
              </div>
            )}

            <form aria-describedby="selectPaymentMethodType">
              <PaymentMethodSwitch
                setSelectedPaymentMethod={setSelectedMethod}
              />
            </form>

            <div className={isPaymentMethodAch(selectedMethod) ? '' : 'hidden'}>
              <Alert
                variant="positive"
                title={t('addPaymentMethod.alert.title')}
                message={t('addPaymentMethod.alert.message')}
              />

              <Divider purpose="neutral" className="my-comfy-xl" />

              <PaymentMethodAddForm
                label={t('addPaymentMethod.form.formTitle.bank')}
                onSubmit={handleAchSubmit}
              >
                <Fieldset id="ach-fields">
                  <PaymentACHFields />
                </Fieldset>
                <AddPaymentMethodSheetFooter
                  isBusy={addPaymentMethod.isPending}
                />
              </PaymentMethodAddForm>
            </div>

            <div
              className={isPaymentMethodCard(selectedMethod) ? '' : 'hidden'}
            >
              <Divider purpose="neutral" className="my-comfy-xl" />
              <PaymentMethodAddForm
                label={t('addPaymentMethod.form.formTitle.card')}
                onSubmit={handleCardSubmit}
              >
                <Fieldset id="cc-fields">
                  <PaymentCCFields />
                </Fieldset>
                <AddPaymentMethodSheetFooter
                  isBusy={addPaymentMethod.isPending}
                />
              </PaymentMethodAddForm>
            </div>

            {!!recurringCommitments?.length && (
              <PaymentMethodAddSheetCommitmentList
                commitments={recurringCommitments}
              />
            )}
          </SheetBody>
        </SheetContent>
      </Sheet>
    </>
  );
};
